/**
 * Tab 2: contractor-scoped chat workspace.
 *
 * Three-panel layout:
 *   - Left: contractor dropdown → contract dropdown → list of
 *     approved (issued) variations under the chosen contract.
 *   - Centre: ``AssistantPanel`` reused verbatim. The session is
 *     bound to the chosen (contractor, contract) via the
 *     session's ``variation_label`` (set to the contract's
 *     reference) and a ``variation_brief`` carrying the contractor
 *     and contract names so the assistant has context without
 *     the operator having to repeat it.
 *   - Right: previous chats for the same (contractor, contract)
 *     scope, filtered client-side from the global chat-session
 *     list.
 *
 * Session-binding strategy. The chat-session schema does not yet
 * carry a ``contract_id`` column; rather than ship a migration
 * for one row, we use the existing ``variation_label`` field as
 * the contract reference. A right-panel session is "for this
 * contract" iff its ``variation_label`` matches the active
 * contract's reference. This trades schema cleanliness for a
 * smaller PR; the field is an exact-match string under operator
 * control on the existing workspace surface, so collisions are
 * essentially impossible in practice.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type ApiError,
  type ApprovedVariationOut,
  type ChatSessionSummary,
  type ContractOut,
  type ContractorOut,
  type DocumentRecord,
  createChatSession,
  listApprovedVariations,
  listChatSessions,
  listContractors,
  listContracts,
  listDocuments,
  loadConfig,
} from "../api/client";
import { useToast } from "../Toasts";
import { AssistantPanel } from "./AssistantPanel";

export function ContractorChatPage() {
  const toast = useToast();
  const [contractors, setContractors] = useState<ContractorOut[]>([]);
  const [contracts, setContracts] = useState<ContractOut[]>([]);
  const [contractorId, setContractorId] = useState<string>("");
  const [contractId, setContractId] = useState<string>("");
  const [variations, setVariations] = useState<ApprovedVariationOut[]>([]);
  const [contractDocs, setContractDocs] = useState<DocumentRecord[]>([]);
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Bootstrap the contractor list once.
  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    listContractors(cfg)
      .then((items) => {
        if (cancelled) return;
        setContractors(items);
        if (items.length > 0) {
          setContractorId((current) => current || items[0].id);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          toast.show("err", err.message ?? "Could not load contractors");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  // Reload contracts whenever the chosen contractor changes.
  useEffect(() => {
    if (!contractorId) {
      setContracts([]);
      setContractId("");
      return;
    }
    let cancelled = false;
    const cfg = loadConfig();
    listContracts(cfg, { contractorId })
      .then((items) => {
        if (cancelled) return;
        setContracts(items);
        const first = items[0]?.id ?? "";
        setContractId((current) =>
          items.some((c) => c.id === current) ? current : first,
        );
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          toast.show("err", err.message ?? "Could not load contracts");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contractorId, toast]);

  // Reload approved variations + previous-chats list + uploaded
  // documents whenever the contract changes or the session-refresh
  // tick fires. The document list flows into the chat session's
  // variation_brief so the assistant knows which files are on file
  // for the active contract; without this, retrieval finds chunks
  // but the assistant has no list of what the operator has actually
  // uploaded to ground claims against.
  useEffect(() => {
    if (!contractId) {
      setVariations([]);
      setContractDocs([]);
      setSessions([]);
      setActiveSessionId(null);
      return;
    }
    let cancelled = false;
    const cfg = loadConfig();
    Promise.all([
      listApprovedVariations(cfg, contractId),
      listChatSessions(cfg),
      listDocuments(cfg),
    ])
      .then(([vars, allSessions, allDocs]) => {
        if (cancelled) return;
        setVariations(vars);
        // Two ways a Document belongs to this Contract:
        //   1. Document.contract_id === Contract.id, set by the
        //      workspace gated upload for every variation submission
        //      and supporting file uploaded under this contract.
        //   2. Document.id === Contract.document_id, the reverse
        //      link the platform uses for the head-contract PDF
        //      itself (the document IS the contract, so the
        //      forward link is implicit). The head doc carries
        //      contract_id=None at the row level; without (2) it
        //      would silently drop out of the chat's Key
        //      documents list and the assistant would dismiss
        //      retrieval hits from it as "not indexed".
        const linkedContract = contracts.find((c) => c.id === contractId);
        const headDocId = linkedContract?.document_id ?? null;
        setContractDocs(
          allDocs.filter(
            (d) =>
              d.contract_id === contractId ||
              (headDocId !== null && d.document_id === headDocId),
          ),
        );
        // Strict scoping: only show contractor (Tab-2) chats whose
        // contract_id matches the active contract. Filtering on
        // kind as well as contract_id matters now that a Workspace
        // chat may also carry a contract_id for retrieval scoping
        // (ADR-0015): without the kind check, a contract-scoped
        // workspace chat would leak into this contractor list.
        const filtered = allSessions.filter(
          (s) => s.kind === "contractor" && s.contract_id === contractId,
        );
        setSessions(filtered);
        // Auto-select the most recent session if none is active.
        setActiveSessionId((current) => {
          if (current && filtered.some((s) => s.session_id === current)) {
            return current;
          }
          if (filtered.length === 0) return null;
          // listChatSessions sorts newest first by updated_at.
          return filtered[0].session_id;
        });
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          toast.show(
            "err",
            err.message ?? "Could not load contract context",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contractId, refreshTick, toast]);

  const selectedContractor = useMemo(
    () => contractors.find((c) => c.id === contractorId) ?? null,
    [contractors, contractorId],
  );
  const selectedContract = useMemo(
    () => contracts.find((c) => c.id === contractId) ?? null,
    [contracts, contractId],
  );

  // Build the createChatSession payload for the active
  // (contractor, contract) scope. Used by + New chat and by
  // AssistantPanel's auto-create path so both flows produce
  // sessions tagged kind:"contractor" (and carrying contract_id as
  // retrieval scope); without the kind tag the kind filter (ADR-0015)
  // would file them on the Workspace chat list instead.
  const buildSessionPayload = useCallback(() => {
    if (!selectedContract || !selectedContractor) {
      return { title: "General chat", variation_label: "" };
    }
    return {
      // Explicit Tab-2 surface tag so this chat is filtered into the
      // contractor list, not the Workspace one (ADR-0015).
      kind: "contractor" as const,
      title: `${selectedContractor.name} — ${selectedContract.reference}`,
      variation_label: selectedContract.reference,
      variation_brief: {
        title: `Discussion about contract ${selectedContract.reference}`,
        contractor: selectedContractor.name,
        contract_reference: selectedContract.reference,
        contract_name: selectedContract.name,
        contract_id: selectedContract.id,
        // Documents the operator has uploaded under this contract.
        // The backend formats them into the system prompt as the
        // "Key documents" block so the assistant knows what files
        // are on file before deciding what to search for.
        documents: contractDocs.map((d) => ({
          name: d.original_filename,
          role: d.doc_type || "document",
          note: "",
        })),
        approved_variations: variations.map((v) => ({
          variation_id: v.variation_id,
          outcome: v.determination_outcome,
          issued_at: v.issued_at,
        })),
      },
    };
  }, [selectedContractor, selectedContract, contractDocs, variations]);

  const startNewChat = useCallback(async () => {
    if (!selectedContract || !selectedContractor || creatingSession) return;
    setCreatingSession(true);
    const cfg = loadConfig();
    try {
      const created = await createChatSession(cfg, buildSessionPayload());
      setActiveSessionId(created.session_id);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.show("err", (err as ApiError).message ?? "Could not start chat");
    } finally {
      setCreatingSession(false);
    }
  }, [
    selectedContractor,
    selectedContract,
    creatingSession,
    buildSessionPayload,
    toast,
  ]);

  // Wiring into AssistantPanel: when it auto-creates a session
  // (e.g. operator types into the composer with no active id), we
  // need to refresh the right-panel listing.
  const onSessionCreated = useCallback((sessionId: string) => {
    setActiveSessionId(sessionId);
    setRefreshTick((t) => t + 1);
  }, []);

  const onMessageSent = useCallback(() => {
    // Touch the session list so its updated_at-driven ordering
    // refreshes.
    setRefreshTick((t) => t + 1);
  }, []);

  const onSessionMissing = useCallback(() => {
    setActiveSessionId(null);
    setRefreshTick((t) => t + 1);
  }, []);

  const onSessionAssessmentLinked = useCallback(() => {
    // Tab 2 chats are not bound to assessments.
  }, []);

  return (
    <div className="contractor-chat">
      <aside className="contractor-chat-left" aria-label="Context">
        <div className="contractor-chat-pickers">
          <div className="field">
            <label htmlFor="cchat-contractor">Contractor</label>
            <select
              id="cchat-contractor"
              value={contractorId}
              onChange={(e) => setContractorId(e.target.value)}
              disabled={contractors.length === 0}
            >
              <option value="">— Select contractor —</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cchat-contract">Contract</label>
            <select
              id="cchat-contract"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              disabled={!contractorId || contracts.length === 0}
            >
              {contracts.length === 0 && (
                <option value="">— No contracts —</option>
              )}
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.reference}
                  {c.name ? ` — ${c.name}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="contractor-chat-context">
          {selectedContract ? (
            <>
              <div className="ccc-section">
                <div className="ccc-label">CONTRACT</div>
                <div className="ccc-row">
                  <span className="ccc-ref">{selectedContract.reference}</span>
                  {selectedContract.name && (
                    <span className="ccc-meta">{selectedContract.name}</span>
                  )}
                </div>
              </div>
              <div className="ccc-section">
                <div className="ccc-label">
                  APPROVED VARIATIONS{" "}
                  <span className="ccc-count">({variations.length})</span>
                </div>
                {variations.length === 0 ? (
                  <div className="ccc-empty">
                    No issued variations under this contract yet.
                  </div>
                ) : (
                  <ul className="ccc-list">
                    {variations.map((v) => (
                      <li key={v.assessment_id} className="ccc-list-item">
                        <span className="ccc-vid">{v.variation_id}</span>
                        {v.determination_outcome && (
                          <span
                            className={`pill pill-outcome pill-outcome-${v.determination_outcome}`}
                          >
                            {v.determination_outcome.replace(/_/g, " ")}
                          </span>
                        )}
                        {v.issued_at && (
                          <span className="ccc-meta">
                            {new Date(v.issued_at).toLocaleDateString("en-AU", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <div className="ccc-empty">
              Pick a contractor and contract to see context here.
            </div>
          )}
        </div>
      </aside>

      <section className="contractor-chat-centre" aria-label="Assistant">
        {selectedContract ? (
          <AssistantPanel
            sessionId={activeSessionId}
            onMessageSent={onMessageSent}
            onSessionMissing={onSessionMissing}
            onSessionCreated={onSessionCreated}
            onSessionAssessmentLinked={onSessionAssessmentLinked}
            getDefaultSessionPayload={buildSessionPayload}
          />
        ) : (
          <div className="page-stub" style={{ flex: 1 }}>
            <div className="page-stub-card">
              <h2>Pick a contract</h2>
              <p>
                Use the dropdowns on the left to choose the contractor
                and contract you want to chat about.
              </p>
            </div>
          </div>
        )}
      </section>

      <aside className="contractor-chat-right" aria-label="Previous chats">
        <div className="col-head chats-head">
          <div className="col-title">CHATS</div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!selectedContract || creatingSession}
            onClick={() => void startNewChat()}
            title={
              !selectedContract
                ? "Pick a contract first"
                : "Start a new chat against the selected contract"
            }
          >
            {creatingSession ? "…" : "+ New chat"}
          </button>
        </div>
        {!selectedContract ? (
          <div className="empty empty-compact">
            <p>Pick a contract to see its previous chats.</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="empty empty-compact">
            <p>No previous chats for this contract. Click "+ New chat" to start one.</p>
          </div>
        ) : (
          <div className="chats-list">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                type="button"
                className="chat-row"
                aria-current={s.session_id === activeSessionId || undefined}
                onClick={() => setActiveSessionId(s.session_id)}
              >
                <span className="chat-row-title">{s.title}</span>
                <span className="chat-row-meta">
                  <span className="chat-row-count">
                    {s.message_count} {s.message_count === 1 ? "msg" : "msgs"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}
